<template>
  	<ValidationProvider 
		tag="div" 
		:vid="vid"
		:name="placeholderText" 
		:rules="rules" 
		v-slot="{ errors, required, ariaInput, ariaMsg }"
		mode="passive"
	>
		<div class="c-input-wrapper c-input-wrapper__select" :class="classes">
			<div v-if="label" class="c-input_label mb-2 d-flex justify-content-between">
				<label class="d-block font_14_19 font_roboto">{{ label }}{{ required ? '*' : '' }}</label>
				<slot name="right-label"></slot>
			</div>
			<small v-if="note" class="">{{ note }}</small>
			<v-select 
				:class="{'vs__multiple': multiple, 'vs__single': !multiple, '--error' : errors[0] }"
				v-model="currentValue" 
				:options="options" 
				:taggable="taggable"
				:multiple="multiple" 
				:clearable="clearable"  
				:placeholder="placeholderText"
				:components="{OpenIndicator, Deselect}"
				:close-on-select="!multiple"
				:searchable="searchable"
				:reduce="reduce"
				v-on:option:created="optionAdded"
				v-on:option:deselected="optionRemoved"
			>
				<template v-slot:no-options><em>{{noResultsText}}</em></template>
			</v-select>
			<span 
				v-if="errors[0]" 
				class="text-error font_14_19"
				v-bind="ariaMsg"
			>
				{{ errors[0] }}
			</span>
		</div>
	</ValidationProvider>
</template>

<script>
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'

export default {
	name: 'InputSelect',
	components: {
		vSelect,
		ValidationProvider
	},
	props: {
		label: {
			type: [String, Boolean],
			default: false
		},
		note: {
			type: String
		},
		rules: {
			type: [String, Object],
			default: ''
		},
		value: {
			type: [String, Array, Object, Number]
		},
		error: {
			type: String
		},
		vid: {
			type: String,
			default: undefined
		},
		options: [Array],
		multiple: {
			type: Boolean,
			default: false
		},
		taggable: {
			type: Boolean,
			default: false
		},
		searchable: {
			type: Boolean,
			default: false
		},
		reduce: {
			default: option => option
		},
		placeholder: {
			type: [String, Boolean],
			default: false
		},
		classes: {
			type: String
		},
		noResultsText: {
			type: String,
			default: 'Sorry, no matching options.'
		},
		clearable: {
			type: Boolean,
			default: false
		}
	},
	data: () => ({
		currentValue: '',
		Deselect: {
			template: `<svg width="8" height="7" viewBox="0 0 8 7">
							<path fill-rule="evenodd" clip-rule="evenodd" d="M3.79297 4.83521L1.62817 7L0.292969 5.66479L2.45776 3.5L0.292969 1.3352L1.62817 0L3.79297 2.16479L5.95776 0L7.29297 1.3352L5.12817 3.5L7.29297 5.66479L5.95776 7L3.79297 4.83521Z" />
						</svg>`
		},
		OpenIndicator: {
			template: `<svg width="9" height="7" viewBox="0 0 9 7" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M1 1.5L4.5 5L8 1.5" stroke="#4B4B4B" stroke-width="1.5"/>
						</svg>`
		},
	}),
	watch: {
		currentValue (val) {
			// allows us to use v-model on our input.
			this.$emit('input', val);
		},
		value (val) {
			this.currentValue = this.value;
		}
	},
	created() {
		this.currentValue = this.value
	},
	computed: {
		placeholderText() {
			return this.placeholder === false ? this.label ? this.label : '' : this.placeholder
		}
	},
	methods: {
		optionAdded(value) {
			this.$emit('optionAdded', value)
		},
		optionRemoved(value) {
			this.$emit('optionRemoved', value)
		},
		// createCity(city) {
		// 	return {
		// 		label: city,
		// 		id: city
		// 	}
		// }
	}
}
</script>

<style lang="scss">
	@import "vue-select/src/scss/vue-select.scss";

	.v-select {
		background: white;

		ul {
			padding: 0 !important;
		}
	}

	.vs__dropdown-menu {
		padding: 0 8px;
		border-left: 1px solid $light-grey;
		border-right: 1px solid $light-grey;
		border-bottom: 1px solid $light-grey;
		box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.04), 0px 4px 10px rgba(0, 0, 0, 0.08);
		border-radius: 4px;
	}

	.vs__dropdown-toggle {
		padding: 0;
		border-radius: 4px;
		border: 1px solid $grey;
	}

	.--error .vs__dropdown-toggle {
		border: 1px solid $error;
	}

	.vs__selected {
		border: 0;
		margin: 0;
		padding: 11px 19px;
		font-size: 14px;
		line-height: 16px;
		color: $black;
		height: 40px;
	}

	.vs__actions {
		padding: 4px 15px 0 3px;
	}

	.vs__clear {
		fill: $black;
		margin-right: 15px;
		width: 20px;
  		height: 20px;

		svg {
			display: block;
			width: 15px;
			height: 13px;
		}
	}

	.vs__selected-options {
		padding: 0;
	}

	.vs__dropdown-option {
		font-size: 14px;
		line-height: 16px;
		padding: 11px 19px;
		color: $black;;
		border-radius: 4px;

		&--highlight,
		&--selected {
			background-color: $ocean-blue;
			color: white;
		}
	}

	.vs__search {
		border: none !important;
		margin: 0 !important;
	}

	.vs__search:focus {
		padding: 11px 19px;
		font-size: 14px;
    	line-height: 16px;
	}

	.vs__open-indicator {
		fill: none;
	}

	.c-input-wrapper {
		margin-bottom: 24px;
	}

	.c-input-wrapper__select {

		&--alternate {
			margin-bottom: 15px;

			.v-select,
			.vs__dropdown-toggle {
				border-radius: 4px;
			}

			.vs__dropdown-toggle {
				border: 1px solid $ocean-blue;
			}
		}
	}

	.vs__multiple {

		.vs__selected {
			padding: 1px 10px;
			height: 25px;
			border: 1px solid $light-grey;
			margin-top: 8px;
			border-radius: 13px;
			background: white;
			margin-left: 10px;
		}
	}

	label {
		color: $black;;
	}

	em {
		opacity: 0.5;
		font-style: italic;
	}
</style>